import {
  normalizePlanFeatures,
  type AiPlanFeatureKey,
} from "@/lib/ai-plan-features";

export type AiLockedCapability =
  | "memory"
  | "sync"
  | "plan_save"
  | "plan_refine"
  | "reports"
  | "premium_reasoning"
  | "advanced_review"
  | "study_actions";

export type AiEntitlementTier = "free" | "plus" | "pro";

export type AiEntitlementState = {
  tier: AiEntitlementTier;
  hasStarterReaderHelp: boolean;
  hasWorkspaceToofi: boolean;
  hasSyncedContinuity: boolean;
  hasMemory: boolean;
  hasPlanSave: boolean;
  hasPlanRefine: boolean;
  hasReports: boolean;
  hasPremiumReasoning: boolean;
  hasStudyActions: boolean;
  hasExtendedHistory: boolean;
  hasAdvancedRecovery: boolean;
  continuityLabel: "Local" | "Synced" | "Synced + extended";
  planningLabel: "Basic" | "Save only" | "Save + refine" | "Refine + recovery";
  reviewLabel: "Basic" | "Light" | "Strong" | "Deep";
  reasoningLabel: "Off" | "Included";
};

const has = (features: Set<AiPlanFeatureKey>, feature: AiPlanFeatureKey) => features.has(feature);

export const getAiEntitlementState = (featureFlags?: string[] | null): AiEntitlementState => {
  const features = normalizePlanFeatures(featureFlags);

  const hasStarterReaderHelp = has(features, "ai_explain") || has(features, "ai_translate");
  const hasWorkspaceToofi = has(features, "workspace_toofi");
  const hasSyncedContinuity = has(features, "chat_sync_workspace") || has(features, "chat_sync_resource");
  const hasMemory = has(features, "ai_memory");
  const hasPlanSave = has(features, "study_plan_save");
  const hasPlanRefine = has(features, "study_plan_refine");
  const hasReports = has(features, "ai_review_reports");
  const hasPremiumReasoning = has(features, "premium_reasoning");
  const hasStudyActions = has(features, "ai_actions");
  const hasGuidedLessons = has(features, "guided_step_lessons");
  const hasExtendedHistory = has(features, "extended_history");
  const hasAdvancedRecovery =
    has(features, "advanced_replan")
    || has(features, "plan_checkins")
    || has(features, "exam_mode");

  const tier: AiEntitlementTier = hasReports || hasPremiumReasoning || hasAdvancedRecovery
    ? "pro"
    : hasMemory || hasSyncedContinuity || hasPlanSave || hasPlanRefine || hasStudyActions || hasGuidedLessons || hasExtendedHistory
      ? "plus"
      : "free";

  const continuityLabel = hasSyncedContinuity
    ? (hasExtendedHistory ? "Synced + extended" : "Synced")
    : "Local";
  const planningLabel = has(features, "plan_checkins")
    ? "Refine + recovery"
    : hasPlanRefine
      ? "Save + refine"
      : hasPlanSave
        ? "Save only"
        : "Basic";
  const reviewLabel = has(features, "deep_review_generation")
    ? "Deep"
    : hasReports
      ? "Strong"
      : hasPlanRefine || hasStudyActions
        ? "Light"
        : "Basic";

  return {
    tier,
    hasStarterReaderHelp,
    hasWorkspaceToofi,
    hasSyncedContinuity,
    hasMemory,
    hasPlanSave,
    hasPlanRefine,
    hasReports,
    hasPremiumReasoning,
    hasStudyActions,
    hasExtendedHistory,
    hasAdvancedRecovery,
    continuityLabel,
    planningLabel,
    reviewLabel,
    reasoningLabel: hasPremiumReasoning ? "Included" : "Off",
  };
};

export const getUpgradeTargetTier = (capability: AiLockedCapability): Extract<AiEntitlementTier, "plus" | "pro"> => {
  switch (capability) {
    case "memory":
    case "sync":
    case "plan_save":
    case "plan_refine":
    case "study_actions":
      return "plus";
    case "reports":
    case "premium_reasoning":
    case "advanced_review":
      return "pro";
  }
};

export const getUpgradeMessage = (
  capability: AiLockedCapability,
  tier: AiEntitlementTier,
): string => {
  switch (capability) {
    case "memory":
      return tier === "free"
        ? "Upgrade to Plus for synced continuity, AI memory, and stronger daily study support."
        : "This plan already includes AI memory.";
    case "sync":
      return tier === "free"
        ? "Upgrade to Plus for synced continuity across your reader, workspace, and saved study flow."
        : "This plan already includes synced continuity.";
    case "plan_save":
      return tier === "free"
        ? "Upgrade to Plus to save your study flow and keep Toofi useful across days."
        : "This plan already includes saved study plans.";
    case "plan_refine":
      return tier === "free"
        ? "Upgrade to Plus for saved plans, refinements, and stronger daily continuity."
        : "This plan already includes plan refinement.";
    case "study_actions":
      return tier === "free"
        ? "Upgrade to Plus for stronger AI actions, synced continuity, and more durable daily help."
        : "This plan already includes stronger AI actions.";
    case "reports":
      return tier === "free"
        ? "Upgrade to Plus for continuity and memory, or Pro for reports and deeper review diagnosis."
        : "Upgrade to Pro for reports and deeper review diagnosis.";
    case "premium_reasoning":
      return tier === "free"
        ? "Upgrade to Pro for advanced reasoning, deeper review help, and stronger recovery support."
        : "Upgrade to Pro for advanced reasoning on harder study tasks.";
    case "advanced_review":
      return tier === "free"
        ? "Upgrade to Pro for deeper review packs, stronger diagnosis, and exam-ready study support."
        : "Upgrade to Pro for deeper review packs and stronger study diagnosis.";
  }
};
